/**
 * `ChatPendingApprovalsBanner` — inline strip on the chat page that
 * surfaces pending approvals scoped to the current session.
 *
 * Shows when `GET /v1/approvals?session_id=...&status=pending` returns
 * one or more rows. Hides itself otherwise (returns `null`) so the
 * common case adds zero vertical space.
 *
 * Polling cadence is 5s while the chat tab has a session open — that's
 * the latency budget for "the model proposed a tool call → operator
 * sees it" before L2 ships an SSE channel. Cheap enough at the
 * volumes we expect (one tab per active session per operator), and
 * automatically pauses when the tab is hidden.
 */

import { ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { ApprovalListResponse } from "@/lib/types";

const POLL_INTERVAL_MS = 5_000;

export function ChatPendingApprovalsBanner({
  sessionId,
}: {
  sessionId: string | null;
}) {
  const t = useT();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async (sid: string) => {
    try {
      // limit=1 — we only need the `total` count for the banner copy.
      const data = await api<ApprovalListResponse>(
        `/approvals?session_id=${encodeURIComponent(sid)}&status=pending&limit=1`,
      );
      setCount(data.total);
    } catch {
      // Banner is decorative; failing the poll silently keeps the
      // chat surface uncluttered.
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setCount(0);
      return;
    }
    let cancelled = false;
    void refresh(sessionId);

    let id: ReturnType<typeof setInterval> | null = null;
    const start = (): void => {
      if (id !== null || cancelled) return;
      id = setInterval(() => {
        void refresh(sessionId);
      }, POLL_INTERVAL_MS);
    };
    const stop = (): void => {
      if (id === null) return;
      clearInterval(id);
      id = null;
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void refresh(sessionId);
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [sessionId, refresh]);

  if (!sessionId || count === 0) return null;

  const message =
    count === 1 ? t("approvals.chat.banner.one") : t("approvals.chat.banner.many", { n: count });

  return (
    <div className="mx-3 mb-2 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[12px] text-amber-700 dark:text-amber-300">
      <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 truncate">{message}</span>
      <Button asChild size="sm" variant="outline" className="h-6 text-[11px]">
        <Link
          to={`/approvals?session_id=${encodeURIComponent(sessionId)}`}
          // The /approvals page reads the `session_id` query for a
          // future deep-link UX; for now it falls through to the full
          // queue — still a one-click path to the right row.
        >
          {t("approvals.chat.banner.cta")}
        </Link>
      </Button>
    </div>
  );
}
